<template>
    <div class="">
        <slot>
            <div class="btn-group-toggle draw-interactions text-center w-100"
                :class="{'btn-group': groupControls}"
                aria-label="Draw Controls">
                <label
                    v-for="control in drawControls"
                    :key="`control-${id}-${control.id}`"
                    :for="`control-${id}-${control.id}`"
                    :title="$t(control.label)"
                    :class="[
                        {'active': control.id === drawControlId},
                        {'disabled': isControlDisabled(control.id) },
                        control.class ? control.class : '']"
                    class="btn btn-outline-main min-w-auto">
                    <input type="radio"
                        :name="`toogle-draw-${id}`"
                        :id="`control-${id}-${control.id}`"
                        :value="control.id"
                        v-model="drawControlId"
                        :disabled="isControlDisabled(control.id)"
                        autocomplete="off">
                    <img v-if="control.image" :src="assetsPathModule(control.image)">
                    <i v-else-if="control.icon" :class="control.icon"></i>
                    <span v-else> {{ control.label }} </span>
                </label>
            </div>
            <div class="row">
                <div class="col">
                    <ul class="small pl-3 mb-0">
                        <li v-html="$t(`${pageID}.actions.draw.messageHtml`)"></li>
                        <li v-html="$t(`${pageID}.actions.snap${enableModify ? '.modify':''}.messageHtml`)"></li>
                        <li v-if="showSelect" v-html="$t(`${pageID}.actions.select.messageHtml`)"></li>
                        <li v-if="showSelect" v-html="$t(`${pageID}.actions.clear.messageHtml`)"></li>
                        <li v-if="enableModify" v-html="$t(`${pageID}.actions.modify.messageHtml`)"></li>
                        <li
                            v-if="showHelpLimit && helpLimitText"
                            v-html="helpLimitText"
                        ></li>
                    </ul>
                </div>
            </div>
            <div class="row mt-3" v-if="messageCurrentControl && messageCurrentControl.length">
                <div class="col">
                    <div class="alert alert-info" role="alert">
                        <button type="button" class="close" data-dismiss="alert" aria-label="Close">
                            <span aria-hidden="true">&times;</span>
                        </button>
                        <ul class="small pl-3 mb-0">
                            <li v-for="(message, idx) in messageCurrentControl"
                                :key="`draw-control-message-${idx}`"
                                v-html="$t(message)">
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
        </slot>
        <LayerFeatureableGalleryModal
            :show="isActiveFeatureableModal"
            :textHeader="featureable.header"
            :groups="featureable.groups"
            size="auto"
            @selected="onSelectedFeatureableModal"
            @close="onCloseFeatureableModal"
        />
    </div>
</template>

<script>
    import Draw, { createBox, createRegularPolygon } from "ol/interaction/Draw";
    import {default as IdentifyInteraction} from '../ol/interaction/Identify';
    import { Select, Modify, Snap } from "ol/interaction";
    import { fromCircle } from "ol/geom/Polygon";
    import { Circle } from "ol/geom";
    import VectorLayer from 'ol/layer/Vector';
    import { Vector as VectorSource } from 'ol/source';
    import { createEditingStyle } from 'ol/style/Style';
    import { extend as extendArray } from 'ol/array';
    import VectorEventType from 'ol/source/VectorEventType';
    import Collection from 'ol/Collection';
    import GeoJSON from 'ol/format/GeoJSON';
    import { capitalizeFirstLetterWord, formatUrl, formatString, isString } from '@config/utils';
    import { isInsideResolutionsLimits } from '../shared/catalog/utils/LayersUtils';
    import { defaultFeatureableFilter } from '../shared/catalog/utils/MapInfoUtils';
    import { MAP_ACTIONS } from '@/app/shared/utils/constants';
    import MessageMixin from '@/app/shared/mixins/MessageMixin';

    let varsOriginal = {
        interactions: {
            draw: null,
            select: null,
            modify: null,
            snap: null,
            alphanumeric: null,
            featureable: null,
        },
        vectorLayer: null,
    };
    let vars = Object.assign({}, varsOriginal);

    export default {
        name: 'DrawInteraction',
        components: {
            LayerFeatureableGalleryModal: () => import(/* webpackChunkName: "featureable-gallery-modal" */ '@/app/shared/components/layers/LayerFeatureableGalleryModal'),
        },
        mixins: [
            MessageMixin,
        ],
        props: {
            id: {
                type: String,
                require: true,
            },
            moduleCode: {
                type: String,
                require: true,
            },
            panelCfg: {
                type: Object,
                default: ()=> {
                    return {};
                },
            },
            url: {
                type: Object,
                default: ()=> {
                    return {};
                },
            },
            value: {
                type: Array,
                default: ()=> [],
            },
            isActive: {
                type: Boolean,
                default: true
            },
            show: {
                type: Boolean,
                default: false
            },
            mapClass: {
                type: Object,
                require: true,
            },
            drawTypes: {
                type: [String, Array],
                required: true,
                validator: function (prop) {
                    let valid = [
                        'point',
                        'text',
                        'line',
                        'freehand',
                        'freehand-shape',
                        'ellipse',
                        'circle',
                        'polygon',
                        'rectangle',
                        'square',
                    ];
                    if(typeof prop === "string"){
                        return valid.includes(prop);
                    }
                    return prop.every(e => valid.includes(e));
                }
            },
            showLabel: {
                type: Boolean,
                default: false
            },
            showSelect: {
                type: Boolean,
                default: false
            },
            showAlphanumeric: {
                type: Boolean,
                default: false
            },
            showFeatureable: {
                type: Boolean,
                default: false
            },
            showClear: {
                type: Boolean,
                default: false
            },
            showHelpLimit: {
                type: Boolean,
                default: true
            },
            minFeatures: {
                type: Number,
                default: 0
            },
            maxFeatures: {
                type: Number,
                default: 1
            },
            enableModify: {
                type: Boolean,
                default: true
            },
            groupControls: {
                type: Boolean,
                default: true
            },
            eventIdentify: {
                type: String,
                default: 'drawinteraction.identifycoord'
            },
        },
        data() {
            let pageID = 'draw_interaction';
            return {
                pageID: pageID,
                loaded: false,
                className: 'ol-draw',
                valueCopy: [],
                drawFeatures: new Collection(),
                drawControlId: undefined,
                availableDrawTypes: [
                    {
                        id: 'point',
                        type: 'Point',
                        icon: 'fas fa-circle',
                        image: '',
                        options:{},
                        label: `${pageID}.actions.draw.point.label`,
                    },
                    {
                        id: 'text',
                        type: 'Text',
                        icon: 'fas fa-circle',
                        image: '',
                        options:{},
                        label: `${pageID}.actions.draw.text.label`,
                    },
                    {
                        id: 'line',
                        type: 'LineString',
                        icon: 'fas fa-slash',
                        image: '',
                        options:{},
                        label: `${pageID}.actions.draw.line.label`,
                        message: `${pageID}.actions.draw.message`,
                    },
                    {
                        id: 'freehand',
                        type: 'LineString',
                        icon: 'fas fa-signature',
                        image: '',
                        options:{
                            freehand: true
                        },
                        label: `${pageID}.actions.draw.freehand.label`,
                        message: `${pageID}.actions.draw.message`,
                    },
                    {
                        id: 'freehand-shape',
                        type: 'Polygon',
                        icon: 'fas fa-cloud',
                        image: '',
                        options:{
                            freehand: true
                        },
                        label: `${pageID}.actions.draw.freehand-shape.label`,
                        message: `${pageID}.actions.draw.message`,
                    },
                    {
                        id: 'ellipse',
                        type: 'Circle',
                        icon: 'far fa-eye',
                        image: '',
                        options:{
                            geometryFunction: function(coordinates, geometry) {
                                var center = coordinates[0];
                                var last = coordinates[1];
                                var dx = center[0] - last[0];
                                var dy = center[1] - last[1];
                                var radius = Math.sqrt(dx * dx + dy * dy);
                                var circle = new Circle(center, radius);
                                var polygon = fromCircle(circle, 64);
                                polygon.scale(dx/radius, dy/radius);
                                if (!geometry) {
                                    geometry = polygon;
                                } else {
                                    geometry.setCoordinates(polygon.getCoordinates());
                                }
                                return geometry;
                            },
                        },
                        label: `${pageID}.actions.draw.ellipse.label`,
                        message: `${pageID}.actions.draw.message`,
                    },
                    {
                        id: 'circle',
                        type: 'Circle',
                        icon: 'far fa-circle',
                        image: '',
                        options:{},
                        label: `${pageID}.actions.draw.circle.label`,
                        message: `${pageID}.actions.draw.message`,
                    },
                    {
                        id: 'polygon',
                        type: 'Polygon',
                        icon: 'fas fa-vector-square',
                        image: '',
                        options:{},
                        label: `${pageID}.actions.draw.polygon.label`,
                        message: `${pageID}.actions.draw.message`,
                    },
                    {
                        id: 'rectangle',
                        type: 'Circle',
                        icon: 'far fa-square',
                        image: '',
                        options:{
                            maxPoints: 2,
                            geometryFunction: createBox(),
                        },
                        label: `${pageID}.actions.draw.rectangle.label`,
                        message: `${pageID}.actions.draw.message`,
                    },
                    {
                        id: 'square',
                        type: 'Circle',
                        icon: 'far fa-square',
                        image: '',
                        options:{
                            geometryFunction: createRegularPolygon(4),
                        },
                        label: `${pageID}.actions.draw.square.label`,
                        message: `${pageID}.actions.draw.message`,
                    },
                ],
                selectControl: {
                    id: 'select',
                    type: 'Select',
                    icon: 'fas fa-mouse-pointer',
                    image: '',
                    label: `${pageID}.actions.select.label`,
                    message: `${pageID}.actions.select.message`,
                    action: MAP_ACTIONS.DRAW_SELECT,
                    // class:'txt-error'
                },
                alphanumericControl: {
                    id: 'alphanumeric',
                    type: 'Alphanumeric',
                    icon: 'fas fa-file-alt',
                    image: '',
                    label: `${pageID}.actions.alphanumeric.label`,
                    message: `${pageID}.actions.alphanumeric.message`,
                    action: MAP_ACTIONS.DRAW_FORM,
                    // class:'txt-error'
                },
                clearControl: {
                    id: 'clear',
                    type: 'Clear',
                    icon: 'fas fa-trash',
                    image: '',
                    label: `${pageID}.actions.clear.label`,
                    class:'txt-error'
                },
                featureableControl: {
                    id: 'featureable',
                    type: 'Featureable',
                    icon: 'fas fa-map-pin',
                    image: '',
                    label: `${pageID}.actions.featureable.label`,
                    message: `${pageID}.actions.featureable.message`,
                    action: MAP_ACTIONS.DRAW_FEATUREABLE,
                    // class:'txt-error'
                },
                featureable: {
                    show: false,
                    header: null,
                    groups: [],
                }
            }
        },
        computed: {
            configMap() {
                return this.moduleCode ? this.$store.getters.getConfigMap(this.moduleCode) : {};
            },
            defaultStyleMap() {
                return this.moduleCode ? this.$store.getters.getDefaultStyleMap(this.moduleCode) : {};
            },
            editingStyleMap() {
                return this.moduleCode ? this.$store.getters.getEditingStyleMap(this.moduleCode) : {};
            },
            drawControls() {
                let types = [...this.drawTypes];
                let controls = this.availableDrawTypes.filter(d => types.includes(d.id)).map(d => {
                    d.action = MAP_ACTIONS.DRAW;
                    return d;
                });
                if(controls.length>0) {
                    if(this.showSelect){
                        controls.splice(0, 0, this.selectControl);
                    }
                    if(this.showFeatureable){
                        controls.push(this.featureableControl);
                    }
                    if(this.showAlphanumeric){
                        controls.push(this.alphanumericControl);
                    }
                    if(this.showClear){
                        controls.push(this.clearControl);
                    }
                }
                return controls;
            },
            editControls() {
                let editControls = [this.selectControl.id, this.clearControl.id];
                if(this.showAlphanumeric && this.alphanumericControl) {
                    editControls.push(this.alphanumericControl.id);
                }
                return editControls;
            },
            currentControl() {
                return this.drawControlId ? this.drawControls.find(c => c.id == this.drawControlId) : null;
            },
            messageCurrentControl() {
                let message =  this.currentControl?.message;
                if(message) {
                    message = this.$t(message);
                    if(isString(message)) {
                        message = [message];
                    }
                    return message;
                }
                return null;
            },
            features(){
                return this.drawFeatures;
            },
            featuresList(){
                return this.features.getArray();
            },
            numFeatures(){
                return this.featuresList.length;
            },
            isLimitFeatures(){
                return this.maxFeatures <= this.numFeatures;
            },
            isActiveFeatureableModal() {
                return this.featureable.show;
            },
            helpLimitText() {
                let limits = [];

                if (this.minFeatures) {
                    limits.push(this.$t(`${this.pageID}.limit.min`, { min: this.minFeatures }));
                }

                if (this.maxFeatures && Number.isFinite(this.maxFeatures) && this.maxFeatures !== Number.MAX_VALUE) {
                    limits.push(this.$t(`${this.pageID}.limit.max`, { max: this.maxFeatures }));
                }

                if (limits.length === 0) return "";

                return this.$t(`${this.pageID}.limit.prefix`) + " " + limits.join(this.$t(`${this.pageID}.limit.and`)) + this.$t(`${this.pageID}.limit.suffix`);
            }
        },
        created() {
            this.initialize();
        },
        mounted() {
            // Here we are following the Vue2 convention on custom v-model:
            // https://github.com/vuejs/vue/issues/2873#issuecomment-223759341
            this.copyValue(this.value);
        },
        beforeDestroy() {
            this.reset();
        },
        methods: {
            initialize: function(){
                if(this.createLayer()){
                    this.addBaseInteractions();
                }
            },
            reset: function(){
                this.removeInteractions();
                this.resetControl();
                let layer = this.layer();
                if(layer){
                    if(this.mapClass){
                        this.mapClass.removeLayer(layer);
                        layer = null;
                    }
                }
                Object.assign(this.$data.drawFeatures, this.$options.data().drawFeatures);
                vars = Object.assign({}, varsOriginal);
            },
            resetControl: function(){
                this.drawControlId=null;
                this.setEventListener(false);
                this.setActionMap(null)
            },
            copyValue (value) {
                this.valueCopy = value;
                this.syncModel();
            },
            syncModel: function(){
                function comparer(otherArray){
                    return function(current){
                        return otherArray.filter(other => other === current).length == 0;
                    }
                }
                let features = this.featuresList;
                let toAdd = this.valueCopy.filter(comparer(features));
                let toRemove = features.filter(comparer(this.valueCopy));

                let layer = this.layer();
                let source = layer?.getSource();
                toRemove.forEach( f => {
                    if(source && source.hasFeature(f)){
                        source.removeFeature(f);
                    }else{
                        this.drawFeatures.remove(f);
                    }
                });
                toAdd.forEach( f => {
                    if(source && !source.hasFeature(f)){
                        source.addFeature(f);
                    }else{
                        this.drawFeatures.push(f);
                    }
                })
            },
            handleCreateLayer: function(evt) {
                this.$emit('onCreateLayer', vars.vectorLayer);
            },
            handleFeatures: function(evt) {
                this.updateInteractionsState();
                this.$emit('input', [...this.featuresList]);
                this.$emit(`on${capitalizeFirstLetterWord(evt.type)}`, {feature: evt.feature, features: [...this.featuresList]});
            },
            handleModify: function(evt) {
                this.$emit('onModify', evt.features ? evt.features.getArray() : []);
            },
            handleSelect: function(evt) {
                let style = null;
                let interactions = vars.interactions;
                if(interactions.select){
                    style = interactions.select.getStyle();
                }
                this.$emit('onSelect', {
                    'selected': evt.selected,
                    'deselected': evt.deselected,
                    'style': style
                });
            },
            handleAlphanumericSelect: function(evt) {
                this.$emit(`onAlphanumeric`, {
                    'feature': evt.selected.length ? evt.selected[0] : null,
                    'deselect': (feat) => {this.deselectFeature(feat);}
                });
            },
            layer: function(){
                return vars.vectorLayer;
            },
            createLayer: function(){
                let layer = this.layer();
                if(!layer){
                    let config = this.configMap.layers.draw;
                    let source = new VectorSource({
                        useSpatialIndex: false,
                        wrapX: config.wrapX !== undefined ? config.wrapX : false, //Wrap the world horizontally on the sketch overlay.
                        features: this.features,
                    });
                    source.on(VectorEventType.ADDFEATURE, (evt) => {
                        // let _source = evt.target;
                        let _feature = evt.feature;
                        _feature.setProperties({'_geom': _feature.getGeometry().getType().toLowerCase()});
                        // if(!_feature.getStyle() && !_feature.getStyleFunction()) {
                        //     _feature.setStyle(layer.getStyle());
                        // }
                        this.handleFeatures(evt);
                    });
                    source.on(VectorEventType.REMOVEFEATURE, (evt) => {
                        this.handleFeatures(evt);
                    });
                    let vectorLayer = new VectorLayer({
                        source: source,
                        visible: true,
                        zIndex: config.zindex,
                        style: config.style ? config.style : this.defaultStyleMap,
                    });
                    this.mapClass.addLayer(vectorLayer);
                    layer = vars.vectorLayer = vectorLayer;
                    this.handleCreateLayer();
                }
                return layer;
            },
            zoomToLayer: function(){
                let layer = this.layer();
                if(layer){
                    let source = layer.getSource();
                    if(source){
                        let featExtent = source.getExtent();
                        if (featExtent) {
                            this.mapClass.fitToExtent(featExtent);
                        }
                    }
                }
            },
            setInteraction: function(id, oldId) {
                let selectedFeatures = [...vars.interactions?.select?.getFeatures()?.getArray() ?? []];
                this.removeDrawInteractions();
                this.disableAllInteractions(false);

                let drawControl = this.drawControls.find(c => c.id === id);
                if(drawControl){
                    switch (drawControl.type.toLowerCase()) {
                        case this.selectControl.id:
                            this.setActiveSelectInteraction(true);
                            break;
                        case this.alphanumericControl.id:
                            this.setActiveAlphanumericInteraction(true);
                            break;
                        case this.featureableControl.id:
                            this.setActiveFeatureableInteraction(true);
                            break;
                        case this.clearControl.id:
                            this.$root.$emit('openConfirmation', {
                                text: this.$t(`${this.pageID}.actions.clear.confirm${selectedFeatures.length>0 ? '.select':''}.message`),
                                id: this.clearControl.id,
                                data: {
                                    id,
                                    oldId,
                                    selectedFeatures
                                },
                                callback: (id, data) => {
                                    this.clearFeatures(data.selectedFeatures);
                                },
                            });

                            if(oldId && id != oldId){
                                this.$nextTick(() => {
                                    this.drawControlId = oldId;
                                });
                            }else{
                                this.resetControl();
                            }
                            break;
                        default:
                            if(!this.isLimitFeatures){
                                this.addDrawInteractions(drawControl);
                            }
                            // this.showMessage('');
                    }
                }

                this.updateUI();
            },
            removeInteractions: function(keys='all'){
                let interactions = vars.interactions;
                for (let key in interactions) {
                    if(keys == 'all' || keys.includes(key)){
                        let interaction = interactions[key];
                        if(interaction){
                            if(this.mapClass){
                                this.mapClass.removeInteraction(interaction);
                            }
                            interactions[key] = null;
                        }
                    }
                }
                this.updateUI();
            },
            addDrawInteractions: function(drawControl){
                this.setActiveBaseInteractions(false);

                let config = this.configMap?.interactions?.draw ?? {};
                let features = this.features;
                // let source = this.layer.getSource();
                // DRAW
                let draw = new Draw({
                    style: config?.style ?? this.editingStyleMap,
                    // source: source,
                    features: features,
                    type: drawControl.type,
                    ...drawControl.options,
                });
                this.mapClass.addInteraction(draw);
                vars.interactions.draw = draw;

                // SNAP
                this.setActiveSnapInteraction(true);

                this.setActionMap(drawControl.action ?? null);
            },
            removeDrawInteractions: function(){
                this.removeInteractions(['draw', 'snap']);
            },
            disableAllInteractions: function(reset=true){
                let interactions = vars.interactions;
                for (let key in interactions) {
                    let interaction = interactions[key];
                    if(interaction){
                        interaction.setActive(false);
                    }
                }

                if(reset){
                    this.setActionMap(null);
                    this.resetControl();
                }
            },
            addBaseInteractions: function(){
                // SELECT
                this.createSelectInteraction();
                // MODIFY
                this.createModifyInteraction();
                // Featureable
                if(this.showFeatureable) {
                    this.createFeatureableInteraction();
                }
                // ALPFANUMERIC
                if(this.showAlphanumeric) {
                    this.createAlphanumericInteraction();
                }
            },
            createSelectInteraction: function(){
                if(this.showSelect){
                    let interactions = vars.interactions;
                    if(!interactions.select){
                        let select = new Select({
                            wrapX: false,
                            multi: false,
                            style: this.getDefaultStyleFunction()
                        });
                        select.setActive(false);
                        select.on('select', (evt) => {
                            this.handleSelect(evt);
                        });
                        select.on('change:active', function (evt) {
                            if(evt.oldValue == true){
                                const selectedFeatures = evt.target.getFeatures();
                                if(selectedFeatures.getLength()>0){
                                    let deselected = [];
                                    for (let i = selectedFeatures.getLength() - 1; i >= 0; --i) {
                                        const feature = selectedFeatures.item(i);
                                        selectedFeatures.remove(feature);
                                        deselected.push(feature);
                                    }
                                    evt.target.dispatchEvent({
                                        type: 'select',
                                        selected: [],
                                        deselected: deselected
                                    });
                                }
                            }
                        });
                        this.mapClass.addInteraction(select);
                        interactions.select = select;
                    }
                }
            },
            createModifyInteraction: function(){
                if(this.enableModify){
                    let interactions = vars.interactions;
                    if(!interactions.modify){
                        let modify = new Modify({
                            features: this.showSelect && interactions.select ?
                                        interactions.select.getFeatures() : this.features,
                            // // the SHIFT key must be pressed to delete vertices, so
                            // // that new vertices can be drawn at the same position
                            // // of existing vertices
                            // deleteCondition: function(mapBrowserEvent) {
                            //     return shiftKeyOnly(mapBrowserEvent) &&
                            //         singleClick(mapBrowserEvent);
                            //      return altKeyOnly(mapBrowserEvent) &&
                            //         singleClick(mapBrowserEvent);
                            // },
                        });
                        modify.on('modifyend', (evt) => {
                            this.handleModify(evt);
                        });
                        modify.setActive(!this.showSelect);
                        this.mapClass.addInteraction(modify);
                        interactions.modify = modify;
                    }
                }
            },
            createAlphanumericInteraction: function(){
                if(this.showAlphanumeric){
                    let interactions = vars.interactions;
                    if(!interactions.alphanumeric){
                        let select = new Select({
                            wrapX: false,
                            multi: false,
                            style: this.getDefaultStyleFunction()
                        });
                        select.setActive(false);
                        select.on('select', (evt) => {
                            this.handleAlphanumericSelect(evt);
                        });
                        select.on('change:active', function (evt) {
                            // if(evt.oldValue == true){
                            //     const selectedFeatures = evt.target.getFeatures();
                            //     if(selectedFeatures.getLength()>0){
                            //         let deselected = [];
                            //         for (let i = selectedFeatures.getLength() - 1; i >= 0; --i) {
                            //             const feature = selectedFeatures.item(i);
                            //             selectedFeatures.remove(feature);
                            //             deselected.push(feature);
                            //         }
                            //         evt.target.dispatchEvent({
                            //             type: 'select',
                            //             selected: [],
                            //             deselected: deselected
                            //         });
                            //     }
                            // }
                        });
                        this.mapClass.addInteraction(select);
                        interactions.alphanumeric = select;
                    }
                }
            },
            createFeatureableInteraction: function(){
                if(this.showFeatureable){
                    let interactions = vars.interactions;
                    if(!interactions.featureable){
                        let interaction = new IdentifyInteraction({event: this.eventIdentify});
                        interaction.on(this.eventIdentify, (evt) => {
                            const _map = this.mapClass;
                            const layers = _map.getLayers().getArray();
                            let featureableLayers = layers.filter((layer) => this.isLayerFeatureable(_map, layer));

                            if (featureableLayers.length === 0) {
                                this.showInfo(`${this.pageID}.msg.featureable.no_layers`);
                                return;
                            }

                            this.loadPropertiesFeatureable(
                                {
                                    id: null,
                                    x: evt.coordinate[0],
                                    y: evt.coordinate[1],
                                    crs: evt.projection.split(':').pop(),
                                    resolution: evt.resolution,
                                    source: evt.target,
                                },
                                evt.params ?? {}
                            );
                        });
                        interaction.setActive(false);
                        this.mapClass.addInteraction(interaction);
                        interactions.featureable = interaction;
                    }
                }
            },
            setActiveSnapInteraction: function(flag){
                flag = !!flag;
                // The snap interaction must be added after the Modify and Draw interactions
                // in order for its map browser event handlers to be fired first. Its handlers
                // are responsible of doing the snapping.
                let interactions = vars.interactions;
                if(interactions.snap){
                    this.removeInteractions(['snap']);
                }
                if(flag) {
                    let features = this.features;
                    // SNAP
                    let snap= new Snap({
                        // source: source,
                        features: features,
                    });
                    snap.setActive(flag);
                    this.mapClass.addInteraction(snap);
                    vars.interactions.snap = snap;
                }
            },
            setActiveSelectInteraction: function(flag){
                flag = !!flag;
                if(this.showSelect){
                    let control = vars.interactions[this.selectControl.id];
                    if(control){
                        this.setActiveModifyInteraction(flag);
                        this.setActionMap(flag ? this.selectControl.action ?? null : null)
                        control.setActive(flag);
                    }
                }
            },
            setActiveModifyInteraction: function(flag){
                flag = !!flag;
                if(this.enableModify){
                    let control = vars.interactions['modify'];
                    if(control){
                        this.setActiveSnapInteraction(flag);
                        control.setActive(flag);
                        this.setEventListener(flag);
                    }
                }
            },
            setActiveAlphanumericInteraction: function(flag){
                flag = !!flag;
                if(this.showAlphanumeric){
                    let control = vars.interactions[this.alphanumericControl.id];
                    if(control){
                        this.setActiveBaseInteractions(false);
                        this.setActionMap(flag ? this.alphanumericControl.action ?? null : null)
                        control.setActive(flag);
                    }
                }
            },
            setActiveFeatureableInteraction: function(flag){
                flag = !!flag;
                if(this.showFeatureable){
                    let control = vars.interactions[this.featureableControl.id];
                    if(control){
                        this.setActiveBaseInteractions(false);
                        this.setActionMap(flag ? this.featureableControl.action ?? null : null)
                        control.setActive(flag);
                    }
                }
            },
            setActiveBaseInteractions: function(flag){
                if(this.showSelect){
                    this.setActiveSelectInteraction(!!flag);
                }
            },
            updateInteractionsState: function() {
                let controls = this.drawControls.filter(c => !this.editControls.includes(c.id));
                if(!controls.find(c => !this.isControlDisabled(c.id))){
                    this.removeDrawInteractions();
                }
                // this.updateUI();
            },
            isControlDisabled: function(id) {
                let disabled = this.editControls.includes(id) && this.numFeatures == 0
                    || this.isLimitFeatures && !this.editControls.includes(id);

                if(disabled && id == this.drawControlId) {
                    this.resetControl();
                }
                // if(disabled) {
                //     let interactions =  vars.interactions;
                //     let interaction = interactions[id];
                //     if(interaction && interaction.getActive()){
                //         interaction.setActive(false);
                //     }
                // }

                return disabled;
            },
            setEventListener: function(flag){
                flag = !!flag;
                if(flag){
                    document.removeEventListener('keydown', this.deleteHandler);
                    document.addEventListener('keydown', this.deleteHandler);
                }else{
                    document.removeEventListener('keydown', this.deleteHandler);
                }
            },
            deleteHandler: function(evt) {
                if(evt instanceof KeyboardEvent) {
                    if (evt.key === 'Delete') {
                        let interation = vars.interactions.select;
                        if(interation) {
                            let features = this.features;
                            let selectedFeatures = interation.getFeatures();
                            selectedFeatures.forEach( f => {
                                features.remove(f);
                            });
                            selectedFeatures.clear();
                        }
                    }
                }
            },
            getDefaultStyleFunction: function() {
                const styles = createEditingStyle();
                extendArray(styles['Polygon'], styles['LineString']);
                extendArray(styles['GeometryCollection'], styles['LineString']);

                return function (feature) {
                    if (!feature.getGeometry()) {
                        return null;
                    }
                    return styles[feature.getGeometry().getType()];
                };
            },
            deselectFeature: function(feature) {
                if(feature) {
                    [vars.interactions.select, vars.interactions.alphanumeric]
                    .filter(n => n)
                    .forEach(interation => {
                        let selectedFeatures = interation.getFeatures();
                        selectedFeatures.remove(feature);
                    });
                }
            },
            clearFeatures: function(selectedFeatures = []){
                let layer = this.layer();
                if(layer){
                    let source = layer.getSource();
                    if(source){
                        let features = source.getFeatures();
                        if(selectedFeatures && selectedFeatures.length > 0) {
                            features = selectedFeatures;
                        }
                        for (let i = 0; i < features.length; i++) {
                            source.removeFeature(features[i])
                        }
                    }
                }
            },
            isLayerFeatureable: function(map, layer) {
                if(!map) return false;

                const _view = map.getOLMapView();
                const currentResolution = _view.getResolution();
                return isInsideResolutionsLimits(layer, currentResolution)
                    && defaultFeatureableFilter(layer);
            },
            loadPropertiesFeatureable(feature = null, params = {}) {
                this.loadFeatureable(feature, params)
                .then(res => {
                    if(res) {
                        const {address, groups = [], x, y, crs, resolution} = res;
                        this.setPropertiesFeatureable(address, groups, x, y, crs, resolution);
                    }else{
                        this.showError(`${this.pageID}.msg.featureable.error`);
                    }
                });
            },
            async loadFeatureable(feature = {}, params = {}) {
                let url = this.url?.featureable;
                if (url) {
                    const {id, x, y, crs, resolution} = feature;
                    return await this.$http
                        .get(formatUrl(url, {x, y, crs, resolution}), { params })
                        .then(res => {
                            return res?.data?.data ?? res?.data ?? res;
                        })
                        .catch(error => {
                            this.showHttpError(error);
                            return null;
                        });
                }
                return Promise.resolve(null);
            },
            async setPropertiesFeatureable(address, groups, x, y, crs, resolution){
                this.featureable.groups = groups ?? [];

                let numFeats = this.featureable.groups.reduce((accumulator, group) => {
                    accumulator = accumulator + (group.features?.featureCollection?.features ?? []).length;
                    return accumulator;
                }, 0);

                this.featureable.show = numFeats>0;

                if(numFeats == 0) {
                    this.showInfo(`${this.pageID}.msg.featureable.no_data`);
                }

                return Promise.resolve(true);
            },
            onCloseFeatureableModal() {
                this.resetFeatureable();
            },
            onSelectedFeatureableModal(params) {
                this.setFeatureFromFeatureable(params);
                this.resetControl();
            },
            setFeatureFromFeatureable(params) {
                this.resetFeatureable();
                const {feature = {}, crs = {}, entity = {}, config = {}, typename = {}, ...rest} = params ?? {};

                let featureProperties = feature.properties ?? {};
                let geometry = feature.geometry ?? {};
                geometry.crs = geometry.crs ?? crs;

                let featureable = {
                    geojson: JSON.parse(JSON.stringify(geometry)),
                    feature: JSON.parse(JSON.stringify(feature)),
                    config: config ?? {},
                    code: typename.property_id ? formatString(typename.property_id, featureProperties, '{', '}') : null,
                    name: typename.property_name ? formatString(typename.property_name, featureProperties, '{', '}') : entity.name,
                    entity_id: entity.id,
                    entity_name: entity.name,
                    entity_image: entity.image,
                    integration_id: config.integration_id,
                    ...rest
                };

                feature.properties = JSON.parse(JSON.stringify({
                    _featureable: featureable,
                    ...featureProperties
                }));

                let layer = this.layer();
                let source = layer?.getSource();
                if(source && !this.isLimitFeatures) {
                    let format = new GeoJSON();
                    let f = format.readFeature(feature);
                    if (f && !source.hasFeature(f)) {
                        source.addFeature(f);
                    }
                }
            },
            resetFeatureable() {
                Object.assign(this.$data.featureable, this.$options.data().featureable);
            },
            forceUpdateComponent() {
                this.updateUI();
                this.resetControl();
            },
            updateUI: function() {
                this.$forceUpdate();
            },
            setActionMap: function(action) {
                this.$store.dispatch(`setActionMap`, {module: this.moduleCode, action});
            },
        },
        watch: {
            value (value) {
                // This watch works from the code side of the 2-way-binding:
                this.copyValue(value);
            },
            show: {
                immediate: false,
                handler(value, oldVal) {
                    if(value){
                        this.initialize();
                    }else{
                        this.reset();
                    }
                },
            },
            drawControlId (value, oldValue) {
                this.setInteraction(value, oldValue);
            },
            isActive: {
                immediate: false,
                handler(value, oldVal) {
                    if(value){
                        this.forceUpdateComponent();
                        this.setActiveBaseInteractions();
                    }else{
                        this.disableAllInteractions();
                    }
                }
            },
        },

    }
</script>

<style lang="scss" scoped>
    .btn-group-toggle.draw-interactions {
        .btn:not(.btn-toggle) {
            font-size: .9rem;
        }
    }
</style>